/**
 * `v-hotkey="keymap"` - run a callback when a key combination is pressed anywhere on the
 * page, unless the user is typing.
 *
 * A port of v-hotkey 0.9, which this replaces. The library is written against vue 2's
 * directive hooks - `bind`, `componentUpdated` and `unbind`, all three of which only reach
 * a hook at all through CUSTOM_DIR, warning on every mount and every update. It went the
 * same way as vue-observe-visibility, which was on the same hooks and threw rather than
 * warned - see vue/directives/observeVisibility.js. vue2-touch-events was the last one on
 * the vue 2 names; it has since been replaced by vue3-touch-events, retiring CUSTOM_DIR.
 *
 * The port is hook for hook what the library did, with three deliberate differences:
 *
 *   - the keymap lives in a context object on the element and is refreshed from `updated`
 *     unconditionally. The library compared `binding.value !== binding.oldValue` and rebuilt
 *     its whole keycode table and both document listeners when they differed - which for a
 *     computed returning an object literal is every single recompute. Nothing is rebound
 *     here, so an update costs one assignment.
 *   - one pair of document listeners for the whole app, attached with the first binding and
 *     dropped with the last, where the library added a pair per bound element.
 *   - matching is on `event.key`, falling back to `event.code`, where the library read the
 *     deprecated `event.keyCode`. The fallback is not decoration: on a mac alt rewrites
 *     `event.key`, so alt+s arrives as 'ß' and every `alt+<letter>` in the app would miss
 *     on `key` alone. `keyCode` was immune to that, so this is parity, not a fix.
 *
 * Semantics carried over unchanged, because call sites depend on them:
 *
 *   - the listeners are on `document`, not on the element the directive sits on. The
 *     element is only a lifetime handle - the shortcut is live for as long as it is
 *     mounted, wherever the focus is - and every bound element sees every key, so two
 *     mounted keymaps both claiming `j` both fire. That is how CommentsListInfiniteScroll
 *     and RenderActivityFeed have always behaved on an activity page.
 *   - nothing fires while the focus is in an INPUT, TEXTAREA or SELECT, or in anything
 *     contenteditable. This is what keeps `ctrl+enter` in AddActivityPanel's keymap from
 *     firing alongside the `@keydown.ctrl.enter` on the comment textarea.
 *   - modifiers match exactly, all four of them: `alt+s` does not fire for ctrl+alt+s, and
 *     `ctrl+alt+f` does not fire for alt+f.
 *   - a combination maps to a function (called on keydown) or to `{keydown, keyup}`. Both
 *     forms are in use here; no call site uses `keyup`.
 *
 * Dropped, because nothing here used them: the `.prevent` and `.stop` directive modifiers,
 * the `Vue.use(VueHotkey, alias)` custom keycode aliases, and the numpad combinations.
 */
const CTX = Symbol('hotkey');
const FORBIDDEN_NODES = ['INPUT', 'TEXTAREA', 'SELECT'];

const MODIFIERS = {
    ctrl: 'ctrlKey',
    control: 'ctrlKey',
    alt: 'altKey',
    option: 'altKey',
    shift: 'shiftKey',
    meta: 'metaKey',
    cmd: 'metaKey',
    command: 'metaKey',
};

/** `event.key` values for the keys whose name in a combination is not what the key reports. */
const NAMED_KEYS = {
    enter: 'enter',
    return: 'enter',
    esc: 'escape',
    escape: 'escape',
    space: ' ',
    tab: 'tab',
    backspace: 'backspace',
    del: 'delete',
    delete: 'delete',
    up: 'arrowup',
    down: 'arrowdown',
    left: 'arrowleft',
    right: 'arrowright',
};

/** Combinations are template literals, so the same handful is parsed over and over. */
const parsed = new Map();

function parse (combination) {
    if (parsed.has(combination)) {
        return parsed.get(combination);
    }
    const result = { key: null, ctrlKey: false, altKey: false, shiftKey: false, metaKey: false };
    for (const token of combination.toLowerCase().replace(/\s/g, '').split('+')) {
        const modifier = MODIFIERS[token];
        if (modifier) {
            result[modifier] = true;
        } else {
            result.key = token;
        }
    }
    parsed.set(combination, result);
    return result;
}

const modifiersMatch = (event, combination) => event.ctrlKey === combination.ctrlKey
    && event.altKey === combination.altKey
    && event.shiftKey === combination.shiftKey
    && event.metaKey === combination.metaKey;

/** The physical key `event.code` reports for a single character, or null for anything else. */
function codeFor (key) {
    if (key >= 'a' && key <= 'z') {
        return `Key${key.toUpperCase()}`;
    }
    if (key >= '0' && key <= '9') {
        return `Digit${key}`;
    }
    return null;
}

function keyMatches (event, key) {
    const expected = NAMED_KEYS[key] || key;
    if (event.key && event.key.toLowerCase() === expected) {
        return true;
    }
    // alt rewrites `event.key` on a mac - alt+s reports 'ß' - so the letter has to be
    // recognised by its position as well. Named keys are unaffected and get no fallback
    return key.length === 1 && event.code === codeFor(key);
}

/**
 * The callback this event should run, or null. Stops at the first combination that matches
 * the key and the modifiers - as the library did - so a map with two entries for the same
 * combination uses the first, and one that matches on keydown only is silent on keyup.
 */
function callbackFor (keymap, event) {
    for (const combination of Object.keys(keymap)) {
        const wanted = parse(combination);
        if (!modifiersMatch(event, wanted) || !keyMatches(event, wanted.key)) {
            continue;
        }
        const handlers = keymap[combination];
        if (typeof handlers === 'function') {
            return event.type === 'keydown' ? handlers : null;
        }
        return handlers[event.type] || null;
    }
    return null;
}

const bound = new Set();

function onKey (event) {
    const { nodeName, isContentEditable } = document.activeElement || {};
    if (isContentEditable || FORBIDDEN_NODES.includes(nodeName)) {
        return;
    }
    // a copy: a callback opens popups and changes routes, which unbinds other elements
    for (const context of [...bound]) {
        const callback = context.keymap && callbackFor(context.keymap, event);
        if (callback) {
            callback(event);
        }
    }
}

function listen (context) {
    if (!bound.size) {
        document.addEventListener('keydown', onKey);
        document.addEventListener('keyup', onKey);
    }
    bound.add(context);
}

function stopListening (context) {
    bound.delete(context);
    if (!bound.size) {
        document.removeEventListener('keydown', onKey);
        document.removeEventListener('keyup', onKey);
    }
}

export default {
    mounted (el, binding) {
        const context = { keymap: binding.value };
        el[CTX] = context;
        listen(context);
    },

    // unconditional: one assignment is cheaper than the reference comparison the library
    // gated on, and far cheaper than what it did when the comparison said yes
    updated (el, binding) {
        if (el[CTX]) {
            el[CTX].keymap = binding.value;
        }
    },

    unmounted (el) {
        const context = el[CTX];
        if (context) {
            // whatever happens, stop calling into a component that is going away
            context.keymap = null;
            stopListening(context);
        }
        delete el[CTX];
    },
};
